 import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, size, replace, cloneDeep, isNull, isUndefined } from "lodash";

import purchaseBudgetService from "@plannel/services/data/purchase-budget-service";
import locationService from "@plannel/services/data/location-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import GridDatePicker from "@plannel/components/GridDatePicker"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";

import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import colorUtil from "../utils/color-util";
import codeUtil from "../utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = colorUtil;
const { hrchyNum } = codeUtil;
const CURRENT_DATE = new ZDate();
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - 5, 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INIT_DATE = { start: START_DATE, end: END_DATE };

const PurchaseBudget = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ? reduxViewState.filter : {});
  const [treeload, setTreeload] = useState(false);
  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : INIT_DATE);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [tPages, setTotalPages] = useState(0);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [purchaseBudgets, setPurchaseBudgets] = useState([]);
  const [locations, setLocations] = useState(reduxViewState?.locations ?? []);
  const [itemHrchies, setItemHrchies] = useState(reduxViewState?.itemHrchies ?? []);

  const currencyMap = useRef({});
  const [csvColDef, setCsvColDef] = useState([]);
  const EXCEL_EXPORT = "excelExport";
  const userData = JSON.parse(localStorage.getItem("user"));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationSearch) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (filterValue) {
      searchFilters.itemHrchyCd = filterValue.category?.hrchyCd;
    }

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrievePurchaseBudget = () => {
    const params = getRequestParams();
    purchaseBudgetService.getAll(params)
      .then(response => {
        const { results, totalPages, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setPurchaseBudgets(results);
        setTotalPages(totalPages);
        
        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reduxViewState) {
          reduxDispatch(reduxUtil.updateViewState({
            ...reduxState,
            ...reduxViewState,
            start: period?.start,
            end: period?.end,
            locationSearch,
            filter: filterValue,
            currentPage,
            pageSize,
            advancedFilters,
          }));
        } 
      
        DataState.initialize(gridRef.current?.api);
        setTreeload(false);
      });
  };

  const retrieveLocations = () => {
    locationService.getLookup({ isLocationMaster: true })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          setLocations(results);
        } else {
          setLocations([]);
        }

        if (reduxViewState) {
          reduxViewState.locations = !isEmpty(results) ? results : [];
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveItemHrchy = () => {
    itemHrchyService.getAll({ searchFilters: { activeFlg: true, hrchyNum: hrchyNum.HRCHY } })
      .then(response => {
        const { results } = response.data;
        setItemHrchies(results);
        if (reduxViewState) {
          reduxViewState.itemHrchies = results;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const removePurchaseBudget = () => {
    const purchaseBudgetIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        purchaseBudgetIds.push(data.id);
      }
    );

    if (purchaseBudgetIds.length > 0) {
      purchaseBudgetService.remove(purchaseBudgetIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrievePurchaseBudget();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const savePurchaseBudget = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      purchaseBudgetService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrievePurchaseBudget();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      savePurchaseBudget();
    } else if (action === "DELETE") {
      removePurchaseBudget();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrievePurchaseBudget();
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      locCd: "",
      itemHrchyCd: "",
      baseDate: ""
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const locationKeyCreator = (params) => {
    return params.value.locCd;
  }


  const itemHrchyKeyCreator = (params) => {
    return params.value.hrchyCd;
  }

  const dropdownValueFormatter = (data) => {
    return data.value ? data.value.name : "";
  }

  const objectComparator = (value1, value2, isDescending) => {
    const val1 = !value1 ? "" : value1.name;
    const val2 = !value2 ? "" : value2.name;
    
    return nullSafeComparator(val1, val2, isDescending);
  };

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.location?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = codeUtil.getCurrencySymbol(currencyCd);
      } 
    }

    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const columnDefs = [
    { headerName: "locCd", field: "location.locCd", colId: "locCd", minWidth: 150, type: ["nonEditableColumn"], flex: 2, cellClass: 'stringType', filterType: 'string'
    },
    {
      headerName: "locName", field: "location",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      keyCreator: locationKeyCreator,
      cellEditorParams: {
        valueFormatter: dropdownValueFormatter,
        values: locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      minWidth: 200,
      flex: 2, 
      filterType: 'string',
    },
    { headerName: "itemHrchyCd", field: "itemHrchy.hrchyCd", colId: "itemHrchyCd", minWidth: 150, type: ["nonEditableColumn"], flex: 2, cellClass: 'stringType' , filterType: 'string' },
    {
      headerName: "hrchyName", field: "itemHrchy",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      keyCreator: itemHrchyKeyCreator,
      cellEditorParams: {
        valueFormatter: dropdownValueFormatter,
        values: itemHrchies
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      minWidth: 300,
      flex: 2, 
      filterType: 'string'
    },
    { headerName: "date", field: "baseDate", 
      type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "month", isFirstDay: true },
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM"), width: 100, filterType: 'date', filterUnit: 'month' },
    { headerName: "budgetAmt", field: "budgetAmt", type: ["numberColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 120, filterType: 'number' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setCsvColDef(cloneDeep(columnDefs))
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!reduxViewState && gridLoading && period) {
      DataState.initialize(gridRef.current?.api);
      retrievePurchaseBudget();
    }

    return () => {
      setPurchaseBudgets([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading]);

  useEffect(() => {
    if (gridLoading && period) {
      retrievePurchaseBudget();
    }

    return () => {
      setPurchaseBudgets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortParams, currentPage, pageSize]);

  useEffect(() => {
    if (gridLoading && period) {
      initPageToRetrieve();
    }

    return () => {
      setPurchaseBudgets([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locationSearch]);

  useEffect(() => {
    if (!isEmpty(locations) && !isEmpty(itemHrchies)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, itemHrchies]);

  useEffect(() => {
    if (gridLoading && filterValue.category?.hrchyCd && period) {
      retrievePurchaseBudget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue])

  useEffect(() => {
    if (gridLoading && period?.start && period?.end) {
      retrievePurchaseBudget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  useEffect(() => {
    setGridLoading(true);
    if (!reduxViewState?.locations) {
      retrieveLocations();
    }
    if (!reduxViewState?.itemHrchies) {
      retrieveItemHrchy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (csvColDef.isEmpty) {
      return;
    }
    for (const col of csvColDef) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueFormatter = null;
        col.valueGetter = params => currencyRenderer(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvColDef])

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <PeriodFilter period={period} setPeriod={setPeriod} dateType="month" />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 240, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent treeload={treeload} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={purchaseBudgetService} columnDefs={csvColDef}
            params={getRequestParams()} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="purchase-budget-grid" className="ag-theme-balham grid-area" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={purchaseBudgets}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              enterNavigatesVertically
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(PurchaseBudget);